import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import LoginModal from './LoginModal';
import { useAuth } from '../../hooks/useAuth';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Listen for custom event to open login modal
  useEffect(() => {
    const handleOpenLoginModal = () => setIsLoginModalOpen(true);
    window.addEventListener('openLoginModal', handleOpenLoginModal);
    return () => window.removeEventListener('openLoginModal', handleOpenLoginModal);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
  }, [location.pathname]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (sectionId: string) => {
    setIsMobileMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    if (location.pathname === '/') {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const handleTestimonialsClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    if (location.pathname === '/testimonials') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/testimonials');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  const handleLogoClick = () => {
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSignOut = async () => {
    setIsUserMenuOpen(false);
    await signOut();
    navigate('/');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" onClick={handleLogoClick} className="flex items-center gap-3 cursor-pointer">
              <img 
                src="https://static.readdy.ai/image/fcb9caa8ea4da54b71dc0a368af215b0/1d6360f6c36e7715a885853386e32dc7.png" 
                alt="iVision Virtual Staging" 
                className="h-10 w-auto"
              />
              <span className="text-xl lg:text-2xl font-bold text-gray-900">iVision Virtual Staging</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              <Link
                to="/"
                onClick={handleLogoClick}
                className="text-sm font-medium hover:text-[#FF6B6B] hover:underline hover:decoration-2 hover:underline-offset-[5.5px] transition-colors cursor-pointer whitespace-nowrap"
              >
                Home
              </Link>
              <a
                href="#services"
                onClick={(e) => handleNavClick(e, 'services')}
                className="text-sm font-medium hover:text-[#FF6B6B] hover:underline hover:decoration-2 hover:underline-offset-[5.5px] transition-colors cursor-pointer whitespace-nowrap"
              >
                Services
              </a>
              <a
                href="#how-it-works"
                onClick={(e) => handleNavClick(e, 'how-it-works')}
                className="text-sm font-medium hover:text-[#FF6B6B] hover:underline hover:decoration-2 hover:underline-offset-[5.5px] transition-colors cursor-pointer whitespace-nowrap"
              >
                How It Works
              </a>
              <a
                href="#pricing"
                onClick={(e) => handleNavClick(e, 'pricing')}
                className="text-sm font-medium hover:text-[#FF6B6B] hover:underline hover:decoration-2 hover:underline-offset-[5.5px] transition-colors cursor-pointer whitespace-nowrap"
              >
                Pricing
              </a>
              <a
                href="#faq"
                onClick={(e) => handleNavClick(e, 'faq')}
                className="text-sm font-medium hover:text-[#FF6B6B] hover:underline hover:decoration-2 hover:underline-offset-[5.5px] transition-colors cursor-pointer whitespace-nowrap"
              >
                FAQ
              </a>
              <a
                href="/testimonials"
                onClick={handleTestimonialsClick}
                className="text-sm font-medium hover:text-[#FF6B6B] hover:underline hover:decoration-2 hover:underline-offset-[5.5px] transition-colors cursor-pointer whitespace-nowrap"
              >
                Testimonials
              </a>
              <a
                href="#contact"
                onClick={(e) => handleNavClick(e, 'contact')}
                className="text-sm font-medium hover:text-[#FF6B6B] hover:underline hover:decoration-2 hover:underline-offset-[5.5px] transition-colors cursor-pointer whitespace-nowrap"
              >
                Contact
              </a>
            </div>

            <div className="hidden lg:flex items-center gap-6">
              {user ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center gap-3 px-4 py-2 rounded-full hover:bg-gray-100 transition-all cursor-pointer"
                  >
                    <div className="w-9 h-9 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-full flex items-center justify-center">
                      <span className="text-sm font-bold text-white">
                        {user.email?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <i className={`ri-arrow-down-s-line text-gray-500 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`}></i>
                  </button>

                  {/* User Dropdown Menu */}
                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-semibold text-gray-900 truncate">{user.email}</p>
                      </div>
                      <button
                        onClick={handleSignOut}
                        className="flex items-center gap-3 w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                      >
                        <i className="ri-logout-box-line text-lg"></i>
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => setIsLoginModalOpen(true)}
                  className="group flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] text-white text-sm font-semibold rounded-full shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-user-line text-base group-hover:rotate-12 transition-transform duration-300"></i>
                  Login
                </button>
              )}
            </div>

            <button 
              onClick={() => setIsMobileMenuOpen(true)}
              className="lg:hidden text-gray-700 w-10 h-10 flex items-center justify-center cursor-pointer"
            >
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>
      )}

      {/* Mobile Menu */}
      <div 
        className={`fixed top-0 right-0 h-full w-80 bg-white shadow-2xl z-50 transform transition-transform duration-300 lg:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <span className="text-lg font-semibold text-gray-900">Menu</span>
            <button 
              onClick={() => setIsMobileMenuOpen(false)}
              className="w-10 h-10 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          {/* User Info (if logged in) */}
          {user && (
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-full flex items-center justify-center">
                  <span className="text-sm font-bold text-white">
                    {user.email?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{user.email}</p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Links */}
          <nav className="flex flex-col gap-2 p-6 flex-1 overflow-y-auto">
            <Link
              to="/"
              onClick={() => {
                handleLogoClick();
                setIsMobileMenuOpen(false);
              }}
              className="flex items-center gap-3 px-4 py-3 text-base font-medium hover:bg-[#F8F6F3] rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-home-line text-xl text-[#FF6B6B]"></i>
              Home
            </Link>
            <a
              href="#services"
              onClick={(e) => {
                handleNavClick(e, 'services');
                setIsMobileMenuOpen(false);
              }}
              className="flex items-center gap-3 px-4 py-3 text-base font-medium hover:bg-[#F8F6F3] rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-service-line text-xl text-[#FF6B6B]"></i>
              Services
            </a>
            <a
              href="#how-it-works"
              onClick={(e) => {
                handleNavClick(e, 'how-it-works');
                setIsMobileMenuOpen(false);
              }}
              className="flex items-center gap-3 px-4 py-3 text-base font-medium hover:bg-[#F8F6F3] rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-lightbulb-line text-xl text-[#FF6B6B]"></i>
              How It Works
            </a>
            <a
              href="#pricing"
              onClick={(e) => {
                handleNavClick(e, 'pricing');
                setIsMobileMenuOpen(false);
              }}
              className="flex items-center gap-3 px-4 py-3 text-base font-medium hover:bg-[#F8F6F3] rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-price-tag-3-line text-xl text-[#FF6B6B]"></i>
              Pricing
            </a>
            <a
              href="#faq"
              onClick={(e) => {
                handleNavClick(e, 'faq');
                setIsMobileMenuOpen(false);
              }}
              className="flex items-center gap-3 px-4 py-3 text-base font-medium hover:bg-[#F8F6F3] rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-question-line text-xl text-[#FF6B6B]"></i>
              FAQ
            </a>
            <a
              href="/testimonials"
              onClick={(e) => {
                handleTestimonialsClick(e);
                setIsMobileMenuOpen(false);
              }}
              className="flex items-center gap-3 px-4 py-3 text-base font-medium hover:bg-[#F8F6F3] rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-star-line text-xl text-[#FF6B6B]"></i>
              Testimonials
            </a>
            <a
              href="#contact"
              onClick={(e) => {
                handleNavClick(e, 'contact');
                setIsMobileMenuOpen(false);
              }}
              className="flex items-center gap-3 px-4 py-3 text-base font-medium hover:bg-[#F8F6F3] rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-contacts-line text-xl text-[#FF6B6B]"></i>
              Contact
            </a>
          </nav>

          {/* Footer CTA */}
          <div className="p-6 border-t border-gray-100">
            {user ? (
              <button 
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  handleSignOut();
                }}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 border-2 border-gray-200 text-gray-700 font-semibold rounded-full hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-logout-box-line text-lg"></i>
                Sign Out
              </button>
            ) : (
              <button 
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setIsLoginModalOpen(true);
                }}
                className="group w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] text-white font-semibold rounded-full shadow-md hover:shadow-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-user-line text-lg group-hover:rotate-12 transition-transform duration-300"></i>
                Login
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Login Modal */}
      <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
    </>
  );
}
